/**
 * One service worker registration, shared by everything that cares about it.
 *
 * `useRegisterSW` registers on mount, so calling it from both the sidebar badge
 * and the update toast would mean two registrations racing each other and two
 * update timers. This provider calls it once and hands the result down.
 *
 * There is deliberately still only one update *mechanism* — the service worker.
 * The Base44 build had three fighting over one database row, one of which
 * showed a blocking modal to every first-time visitor.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

/** How often to ask the browser whether a newer build has been deployed. */
const CHECK_INTERVAL_MS = 60 * 60 * 1000;

/** Baked in by Vite from the workspace root package.json. */
export const APP_VERSION = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '0.0.0';

/** How long "Reload now" waits for the new build to take over before reloading anyway. */
const APPLY_TIMEOUT_MS = 15 * 1000;

/** The entry script a page was built with — it changes with every build. */
function entryScript(doc) {
  const script = doc.querySelector('script[type="module"][src*="/assets/"]');
  return script ? new URL(script.getAttribute('src'), window.location.origin).pathname : null;
}

/**
 * Whether this page is running an older build than the server now serves.
 *
 * A waiting or newly active service worker does not by itself mean the page
 * is out of date. Refreshing a deep link after a deploy fetches the new page
 * from the network while the new worker is still waiting, and a page reloaded
 * onto a new build can see that build take over a moment later. Prompting
 * either one was the "update available" that came straight back after
 * updating.
 *
 * The query string keeps the request clear of the service worker's precached
 * copy of `/`, which is the active worker's build rather than the server's.
 */
async function isPageStale() {
  try {
    const response = await fetch(`/?build-check=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'same-origin',
    });
    if (!response.ok) return true;
    const latest = new DOMParser().parseFromString(await response.text(), 'text/html');
    const mine = entryScript(document);
    const theirs = entryScript(latest);
    return !mine || !theirs || mine !== theirs;
  } catch {
    // Offline: cannot tell, and saying so costs one extra prompt at most.
    return true;
  }
}

const UpdateContext = createContext({
  updateReady: false,
  updating: false,
  applyUpdate: () => {},
  dismissUpdate: () => {},
  checkForUpdates: () => {},
});

export function UpdateProvider({ children }) {
  const registrationRef = useRef(null);
  // Set when this tab asked for the update. Only that tab reloads at the
  // takeover; any other tab may be halfway through a form.
  const requestedRef = useRef(false);
  const [updateReady, setUpdateReady] = useState(false);
  const [updating, setUpdating] = useState(false);

  // The plugin's own `needRefresh` flag is not used: it is set for any waiting
  // worker, including one whose build this page is already running.
  useRegisterSW({
    async onNeedRefresh() {
      if (await isPageStale()) {
        console.info('A newer StockWise build is ready to install.');
        setUpdateReady(true);
        return;
      }
      // Already running the new build, so there is nothing to reload into.
      // Let the waiting worker take over quietly; the takeover handler below
      // finds this page current and stays silent.
      const registration = await navigator.serviceWorker.getRegistration();
      registration?.waiting?.postMessage({ type: 'SKIP_WAITING' });
    },
    // Left alone, the plugin reloads every tab that saw the prompt the moment
    // any one of them accepts it. The takeover handler below decides instead.
    onNeedReload() {},
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      registrationRef.current = registration;

      // Service workers only check for a new build on navigation by default,
      // and this is a single-page app — someone can sit on the same tab for
      // days. Check on a timer and whenever the tab is brought back.
      const check = () => {
        if (document.visibilityState === 'visible') registration.update();
      };

      const timer = setInterval(check, CHECK_INTERVAL_MS);
      document.addEventListener('visibilitychange', check);

      // The registration outlives the page, so this is cleaned up on unload
      // rather than by React.
      window.addEventListener('beforeunload', () => {
        clearInterval(timer);
        document.removeEventListener('visibilitychange', check);
      });
    },
    onRegisterError(error) {
      // Not fatal: without a service worker the app still works, it just will
      // not run offline or announce updates.
      console.warn('Service worker registration failed:', error?.message ?? error);
    },
  });

  useEffect(() => {
    // This page booted, so if a previous chunk failure forced a reload, the
    // reload worked. Arm the retry again for next time.
    try {
      sessionStorage.removeItem('stockwise.chunk-reloaded');
    } catch {
      // Storage unavailable; the flag never persisted either.
    }

    if (!('serviceWorker' in navigator)) return undefined;

    // The browser may activate a waiting worker on its own — a full
    // navigation while one is waiting is enough, and so is "Reload now" in
    // another tab — and with clientsClaim it then adopts this page. The tab
    // that asked reloads onto the new build. Every other tab is told instead
    // of being reloaded out from under whatever it was typing; if it moves to
    // another page first, the missing-chunk guard in routes.jsx reloads it.
    let hadController = Boolean(navigator.serviceWorker.controller);
    const onTakeover = async () => {
      if (requestedRef.current) {
        window.location.reload();
        return;
      }
      if (!hadController) {
        // First install claiming a previously uncontrolled page — benign.
        hadController = true;
        return;
      }
      if (await isPageStale()) setUpdateReady(true);
    };

    navigator.serviceWorker.addEventListener('controllerchange', onTakeover);
    return () => navigator.serviceWorker.removeEventListener('controllerchange', onTakeover);
  }, []);

  /**
   * Ask the browser whether a newer build exists, at most once per interval.
   * Route changes call this — an hourly timer alone meant a deploy could sit
   * unannounced for most of an hour and then take a tab by surprise.
   */
  const lastCheck = useRef(0);
  const checkForUpdates = useCallback(() => {
    const now = Date.now();
    if (now - lastCheck.current < 5 * 60 * 1000) return;
    lastCheck.current = now;
    registrationRef.current?.update().catch(() => {
      // Offline or mid-deploy; the timer will try again.
    });
  }, []);

  const applyUpdate = useCallback(() => {
    requestedRef.current = true;
    setUpdating(true);

    const registration = registrationRef.current;
    // Nothing waiting means the new build already took over — from another
    // tab, or the browser on its own. Reloading is all that is left to do.
    if (!registration?.waiting) {
      window.location.reload();
      return;
    }

    // The normal path: the waiting worker takes over, "controllerchange"
    // fires, and the handler above reloads. This used to reload blindly after
    // four seconds as a fallback; a phone waking the new worker can take
    // longer than that, so the reload landed on the old build, the prompt came
    // back, and the takeover then announced an update a second time. Instead,
    // keep asking until the worker has actually stopped waiting.
    const ask = () => registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
    ask();

    let seconds = 0;
    const timer = setInterval(() => {
      seconds += 1;
      if (!registration.waiting || seconds * 1000 >= APPLY_TIMEOUT_MS) {
        // Activated (or stuck): either way the person asked for a reload,
        // and a dead button is the one unacceptable outcome.
        clearInterval(timer);
        window.location.reload();
      } else if (seconds % 3 === 0) {
        ask();
      }
    }, 1000);
  }, []);

  const value = useMemo(
    () => ({
      updateReady,
      updating,
      applyUpdate,
      dismissUpdate: () => setUpdateReady(false),
      checkForUpdates,
    }),
    [updateReady, updating, applyUpdate, checkForUpdates],
  );

  return <UpdateContext.Provider value={value}>{children}</UpdateContext.Provider>;
}

export function useAppUpdate() {
  return useContext(UpdateContext);
}
